import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { formatDistanceToNow, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { ClipboardList } from "lucide-react";

const DECISION_COLOR = {
  approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  rejected: "bg-red-500/15 text-red-300 border-red-500/30",
};

function summarize(args) {
  if (!args || typeof args !== "object") return "—";
  const keys = ["name", "title", "email", "deal_id", "prompt", "aspect_ratio"];
  const parts = [];
  for (const k of keys) {
    if (args[k]) parts.push(`${k}: ${String(args[k]).slice(0, 60)}`);
  }
  return parts.join(" · ") || JSON.stringify(args).slice(0, 120);
}

function resultSummary(r) {
  if (!r) return "—";
  if (r.error) return `Fehler: ${r.error}`;
  if (r.action === "generated") return `Bild erzeugt (${r.aspect_ratio})`;
  if (r.action === "created") return "Erstellt";
  if (r.action === "updated") return "Aktualisiert";
  return typeof r === "object" ? Object.keys(r).join(", ") : String(r);
}

export default function Protokoll() {
  const [rows, setRows] = useState([]);
  const [actions, setActions] = useState([]);
  const [action, setAction] = useState("__all__");
  const [decision, setDecision] = useState("__all__");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (action !== "__all__") params.set("action", action);
      if (decision !== "__all__") params.set("decision", decision);
      const q = params.toString() ? `?${params.toString()}` : "";
      const [{ data }, { data: acts }] = await Promise.all([
        api.get(`/audit-logs${q}`), api.get("/audit-logs/actions"),
      ]);
      setRows(data); setActions(acts);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [action, decision]);

  const stats = useMemo(() => {
    const approved = rows.filter((r) => r.decision === "approved").length;
    const rejected = rows.filter((r) => r.decision === "rejected").length;
    return { approved, rejected };
  }, [rows]);

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="protokoll-page">
      <header className="mb-6">
        <div className="text-xs font-semibold uppercase tracking-wider text-emerald-400">Audit</div>
        <h1 className="text-3xl font-extrabold tracking-tight mt-1 text-white">Protokoll</h1>
        <p className="text-sm text-slate-400 mt-2">
          Jede Entscheidung des KI-Agenten — transparent nachlesbar.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">Aktion</span>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="w-56 bg-white/5 border-white/10 text-slate-200" data-testid="protokoll-action-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Alle</SelectItem>
              {actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">Entscheidung</span>
          <Select value={decision} onValueChange={setDecision}>
            <SelectTrigger className="w-40 bg-white/5 border-white/10 text-slate-200" data-testid="protokoll-decision-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Alle</SelectItem>
              <SelectItem value="approved">Genehmigt</SelectItem>
              <SelectItem value="rejected">Abgelehnt</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 border font-normal" data-testid="protokoll-approved-count">
          {stats.approved} genehmigt
        </Badge>
        <Badge className="bg-red-500/15 text-red-300 border-red-500/30 border font-normal" data-testid="protokoll-rejected-count">
          {stats.rejected} abgelehnt
        </Badge>
      </div>

      <Card className="bg-[#111827] border-white/5">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-slate-400">Lade…</div>
          ) : rows.length === 0 ? (
            <div className="p-16 text-center" data-testid="protokoll-empty">
              <ClipboardList className="h-10 w-10 text-slate-600 mx-auto mb-3" />
              <div className="text-white font-semibold mb-1">Noch keine Einträge</div>
              <div className="text-sm text-slate-400">Entscheidungen des KI-Agenten erscheinen hier.</div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-slate-400">Zeitpunkt</TableHead>
                  <TableHead className="text-slate-400">Modell</TableHead>
                  <TableHead className="text-slate-400">Aktion</TableHead>
                  <TableHead className="text-slate-400">Parameter</TableHead>
                  <TableHead className="text-slate-400">Entscheidung</TableHead>
                  <TableHead className="text-slate-400">Ergebnis</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className="border-white/5 hover:bg-white/[0.02]" data-testid={`audit-row-${r.id}`}>
                    <TableCell className="text-slate-300 text-xs whitespace-nowrap">
                      {r.timestamp ? formatDistanceToNow(parseISO(r.timestamp), { addSuffix: true, locale: de }) : "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-white/10 text-slate-300 text-[10px]">{r.model || "—"}</Badge>
                    </TableCell>
                    <TableCell className="text-white font-mono text-xs">{r.action}</TableCell>
                    <TableCell className="text-slate-400 text-xs max-w-[320px] truncate">{summarize(r.arguments)}</TableCell>
                    <TableCell>
                      <Badge className={`${DECISION_COLOR[r.decision] || "bg-slate-500/15 text-slate-300 border-slate-500/20"} border font-normal`}>
                        {r.decision === "approved" ? "Genehmigt" : r.decision === "rejected" ? "Abgelehnt" : r.decision}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-400 text-xs max-w-[260px] truncate">{resultSummary(r.result)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
